/**
 * HUD stylesheet.
 *
 * One scoped string, injected once, exactly as `ui/overlay.js` does it — the
 * project has no CSS files and no build step for them, and a second convention
 * would only be a second place to look.
 *
 * The palette is Lineage II's: slate panels, amber chrome, rose HP, sky MP,
 * amber CP. `#hud` sits at z-index 70, above the hint and below the settings
 * overlay, and is `pointer-events: none` so the snow stays clickable — every
 * panel turns them back on for itself.
 */

export const HUD_CSS = `
#hud {
  position: fixed; inset: 0; z-index: 70; pointer-events: none;
  font: 400 12px/1.4 ui-sans-serif, Inter, "Segoe UI", system-ui, sans-serif;
  color: #e2e8f0; user-select: none;
}
#hud .panel {
  position: absolute; pointer-events: auto;
  background: rgba(2, 6, 23, 0.9);
  border: 1px solid rgba(51, 65, 85, 0.8);
  border-radius: 10px;
  backdrop-filter: blur(8px) saturate(1.1);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.45);
}
#hud .mono { font-family: ui-monospace, "SF Mono", "Cascadia Mono", Menlo, monospace;
  font-variant-numeric: tabular-nums; }
#hud b { font-weight: 600; }

/* ------------------------------------------------------------ bars */
#hud .track {
  width: 100%; background: #1e293b; border: 1px solid rgba(51,65,85,0.9);
  border-radius: 4px; padding: 1px; overflow: hidden;
}
#hud .fill { height: 100%; border-radius: 3px; transition: width 140ms linear; }
#hud .fill.cp { background: linear-gradient(90deg, #d97706, #fbbf24); }
#hud .fill.hp { background: linear-gradient(90deg, #b91c1c, #ef4444); }
#hud .fill.mp { background: linear-gradient(90deg, #2563eb, #38bdf8); }
#hud .fill.xp { background: #34d399; }
#hud .fill.cast { background: linear-gradient(90deg, #f59e0b, #fde68a); }
#hud .barlabel { display: flex; justify-content: space-between; font-size: 10px; }

/* -------------------------------------------------- player status */
#hud-status { top: 12px; left: 12px; width: 288px; padding: 10px 12px; }
#hud-status .top {
  display: flex; justify-content: space-between; align-items: flex-start;
  border-bottom: 1px solid #1e293b; padding-bottom: 8px; margin-bottom: 8px;
}
#hud-status .lvl {
  background: rgba(245, 158, 11, 0.2); color: #fcd34d; border: 1px solid rgba(245,158,11,0.4);
  border-radius: 4px; padding: 2px 7px; font-size: 11px; font-weight: 700;
}
#hud-status .who { flex: 1; margin-left: 10px; }
#hud-status .who b { font-size: 13px; color: #f1f5f9; display: block; }
#hud-status .who i { font-style: normal; font-size: 11px; color: rgba(251,191,36,0.9); }
#hud-status .purse { text-align: right; font-size: 10px; color: #94a3b8; line-height: 1.5; }
#hud-status .purse span { color: #fbbf24; }
#hud-status .bars > div { margin-bottom: 5px; }
#hud-status .buffs { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 8px;
  padding-top: 8px; border-top: 1px solid #1e293b; min-height: 12px; }
#hud .chip {
  display: inline-flex; align-items: center; gap: 4px; font-size: 10px;
  border-radius: 4px; padding: 2px 6px;
  background: rgba(12, 74, 110, 0.6); border: 1px solid rgba(56,189,248,0.6); color: #bae6fd;
}
#hud .chip.amber { background: rgba(120,53,15,0.6); border-color: rgba(251,191,36,0.6); color: #fde68a; }

/* ------------------------------------------------------ target bar */
#hud-target { top: 12px; left: 50%; transform: translateX(-50%); width: 320px; padding: 8px 10px; }
#hud-target .row { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
#hud-target .name { flex: 1; font-size: 13px; font-weight: 600; overflow: hidden;
  text-overflow: ellipsis; white-space: nowrap; }
#hud-target .badge { font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase;
  border-radius: 4px; padding: 2px 6px; border: 1px solid; }
#hud-target .badge.passive { background: #334155; color: #e2e8f0; border-color: #475569; }
#hud-target .badge.aggressive { background: rgba(127,29,29,0.8); color: #fca5a5; border-color: #dc2626; }
#hud-target .badge.boss { background: rgba(120,53,15,0.9); color: #fcd34d; border-color: #f59e0b;
  font-weight: 700; }
#hud-target .badge.npc { background: rgba(12,74,110,0.7); color: #7dd3fc; border-color: #0ea5e9; }
#hud-target.npc { border-color: rgba(2,132,199,0.6); }
#hud-target .hint { font-size: 11px; color: #94a3b8; font-style: italic; margin-top: 4px; }

/* --------------------------------------------------------- hotbar */
#hud-hotbar { bottom: 14px; left: 50%; transform: translateX(-50%); padding: 8px; }
#hud-hotbar .slots { display: grid; grid-template-columns: repeat(12, 48px); gap: 6px; }
#hud .slot {
  position: relative; width: 48px; height: 48px; border-radius: 8px;
  background: rgba(15, 23, 42, 0.9); border: 1px solid rgba(51,65,85,0.8);
  display: flex; flex-direction: column; align-items: center; justify-content: space-between;
  padding: 2px; cursor: pointer; transition: border-color 120ms ease, background 120ms ease;
}
#hud .slot:hover { border-color: #fbbf24; background: #1e293b; }
#hud .slot.on { background: rgba(12,74,110,0.8); border-color: #38bdf8;
  box-shadow: 0 0 0 2px rgba(56,189,248,0.35); }
#hud .slot.locked { opacity: 0.45; cursor: default; }
#hud .slot .key { font-size: 9px; font-weight: 700; color: #fbbf24; }
#hud .slot .icon { font-size: 19px; line-height: 1; }
#hud .slot .qty { font-size: 9px; color: #cbd5e1; }
#hud .slot .cd {
  position: absolute; inset: 0; border-radius: 8px; background: rgba(2,6,23,0.72);
  color: #fde68a; font-size: 12px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; pointer-events: none;
}

/* -------------------------------------------------------- cast bar */
#hud-cast { bottom: 78px; left: 50%; transform: translateX(-50%); width: 260px;
  padding: 6px 10px 8px; display: none; }
#hud-cast.show { display: block; }
#hud-cast .name { font-size: 11px; color: #fde68a; margin-bottom: 4px; text-align: center; }

/* ------------------------------------------------------ action dock */
#hud-dock { bottom: 14px; right: 12px; padding: 6px; display: flex; gap: 4px; }
#hud-dock button {
  width: 46px; height: 46px; border-radius: 8px; cursor: pointer;
  background: #0f172a; border: 1px solid #334155; color: #e2e8f0;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
  font: inherit; transition: border-color 120ms ease, background 120ms ease;
}
#hud-dock button:hover { border-color: #fbbf24; background: #1e293b; }
#hud-dock button .g { font-size: 16px; line-height: 1; }
#hud-dock button .t { font-size: 8px; font-weight: 700; color: #94a3b8; letter-spacing: 0.04em; }

/* --------------------------------------------------------- minimap */
#hud-map { top: 12px; right: 12px; width: 148px; height: 148px; border-radius: 50%;
  overflow: hidden; border: 2px solid rgba(245,158,11,0.4); }
#hud-map .ring { position: absolute; inset: 14px; border-radius: 50%;
  border: 1px solid rgba(30,41,59,0.9); opacity: 0.5; }
#hud-map .cross { position: absolute; background: #1e293b; }
#hud-map .cross.h { left: 8px; right: 8px; top: 50%; height: 1px; }
#hud-map .cross.v { top: 8px; bottom: 8px; left: 50%; width: 1px; }
#hud-map .compass { position: absolute; font-size: 9px; font-weight: 700; color: #64748b; }
#hud-map .compass.n { top: 3px; left: 50%; transform: translateX(-50%); color: #fbbf24; }
#hud-map .compass.s { bottom: 3px; left: 50%; transform: translateX(-50%); }
#hud-map .compass.w { left: 5px; top: 50%; transform: translateY(-50%); }
#hud-map .compass.e { right: 5px; top: 50%; transform: translateY(-50%); }
#hud-map .blip { position: absolute; border-radius: 50%; transform: translate(-50%, -50%); }
#hud-map .blip.mob { width: 7px; height: 7px; background: #f43f5e; }
#hud-map .blip.boss { width: 11px; height: 11px; background: #fbbf24; border: 1px solid #fde68a; }
#hud-map .blip.npc { width: 8px; height: 8px; background: #fbbf24; border: 1px solid #020617; }
#hud-map .blip.sel { background: #fde047; box-shadow: 0 0 0 3px rgba(250,204,21,0.4); }
#hud-map .blip.me { width: 10px; height: 10px; background: #34d399; border: 2px solid #fff; }

/* ------------------------------------------------------------ chat */
#hud-chat { bottom: 14px; left: 12px; width: 380px; height: 214px;
  padding: 8px; display: flex; flex-direction: column; }
#hud-chat .tabs { display: flex; gap: 4px; border-bottom: 1px solid #1e293b;
  padding-bottom: 6px; margin-bottom: 6px; }
#hud-chat .tabs button {
  background: none; border: none; color: #94a3b8; cursor: pointer; font: inherit;
  font-size: 11px; font-weight: 600; text-transform: capitalize;
  padding: 2px 8px; border-radius: 4px;
}
#hud-chat .tabs button:hover { color: #e2e8f0; background: #1e293b; }
#hud-chat .tabs button.on { background: #f59e0b; color: #020617; font-weight: 700; }
#hud-chat .log { flex: 1; overflow-y: auto; font-size: 12px; padding-right: 4px; }
#hud-chat .log::-webkit-scrollbar { width: 6px; }
#hud-chat .log::-webkit-scrollbar-thumb { background: #334155; border-radius: 3px; }
#hud-chat .line { margin-bottom: 2px; word-wrap: break-word; }
#hud-chat .line .ts { font-size: 10px; color: #64748b; margin-right: 5px; }
#hud-chat .line.system { color: #fcd34d; }
#hud-chat .line.damage { color: #fb7185; font-family: ui-monospace, Menlo, monospace; }
#hud-chat .line.trade { color: #f472b6; }
#hud-chat .line.party { color: #7dd3fc; }
#hud-chat .line.clan { color: #6ee7b7; }
#hud-chat form { display: flex; gap: 5px; margin-top: 6px; padding-top: 6px;
  border-top: 1px solid #1e293b; }
#hud-chat input {
  flex: 1; background: #0f172a; border: 1px solid #334155; border-radius: 4px;
  color: #e2e8f0; padding: 4px 7px; font: inherit; font-size: 12px; outline: none;
}
#hud-chat input:focus { border-color: #fbbf24; }
#hud-chat button.send {
  background: #f59e0b; color: #020617; border: none; border-radius: 4px;
  padding: 0 10px; font-weight: 700; cursor: pointer;
}

/* -------------------------------------------------- floating text */
#hud-float { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
#hud-float div {
  position: absolute; transform: translate(-50%, -50%); white-space: nowrap;
  font-weight: 800; font-size: 15px; letter-spacing: 0.01em;
  -webkit-text-stroke: 3px rgba(2, 6, 23, 0.85); paint-order: stroke fill;
}
#hud-float div.big { font-size: 22px; }

/* ------------------------------------------------------ name plates */
#hud-plates { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
#hud-plates .plate {
  position: absolute; transform: translate(-50%, -100%); text-align: center;
  white-space: nowrap; font-size: 11px; line-height: 1.25;
  text-shadow: 0 1px 3px #000, 0 0 8px rgba(0,0,0,0.9);
}
#hud-plates .plate .nm { color: #e2e8f0; }
#hud-plates .plate.aggressive .nm { color: #fca5a5; }
#hud-plates .plate.boss .nm { color: #fcd34d; font-weight: 700; }
#hud-plates .plate.npc .nm { color: #7dd3fc; }
#hud-plates .plate.sel .nm { color: #fde047; font-weight: 700; }
#hud-plates .plate .lv { color: #94a3b8; font-size: 10px; }
#hud-plates .plate .hp {
  width: 62px; height: 3px; margin: 2px auto 0; border-radius: 2px;
  background: rgba(2,6,23,0.85); border: 1px solid rgba(51,65,85,0.9); overflow: hidden;
}
#hud-plates .plate .hp i { display: block; height: 100%; background: #ef4444; }

/* --------------------------------------------------------- modals */
#hud .backdrop {
  position: absolute; inset: 0; pointer-events: auto;
  background: rgba(0, 0, 0, 0.6); backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center; padding: 16px;
}
#hud .modal {
  background: #020617; border: 1px solid rgba(51,65,85,0.9); border-radius: 14px;
  box-shadow: 0 30px 70px rgba(0,0,0,0.6); width: 100%; max-width: 520px;
  max-height: 86vh; display: flex; flex-direction: column; overflow: hidden;
}
#hud .modal.wide { max-width: 660px; }
#hud .modal.narrow { max-width: 420px; }
#hud .modal > header {
  background: #0f172a; border-bottom: 1px solid #1e293b;
  padding: 11px 16px; display: flex; align-items: center; justify-content: space-between;
}
#hud .modal > header b { color: #fbbf24; font-size: 14px; letter-spacing: 0.02em; }
#hud .modal > header .sub { font-size: 11px; color: #94a3b8; margin-left: 10px; }
#hud .modal > header button {
  background: none; border: none; color: #94a3b8; cursor: pointer;
  font-size: 18px; line-height: 1; padding: 0 2px;
}
#hud .modal > header button:hover { color: #f8fafc; }
#hud .modal .body { padding: 16px; overflow-y: auto; }
#hud .modal .body::-webkit-scrollbar { width: 8px; }
#hud .modal .body::-webkit-scrollbar-thumb { background: #334155; border-radius: 4px; }
#hud .modal > footer {
  background: #0f172a; border-top: 1px solid #1e293b; padding: 10px 16px;
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
}
#hud .modal > footer .note { font-size: 11px; color: #64748b; }
#hud .btn {
  background: #f59e0b; color: #020617; border: none; border-radius: 5px;
  padding: 6px 14px; font: inherit; font-size: 12px; font-weight: 700; cursor: pointer;
}
#hud .btn:hover { background: #fbbf24; }
#hud .btn.ghost { background: #1e293b; color: #cbd5e1; }
#hud .btn.ghost:hover { background: #334155; }
#hud .btn.danger { background: rgba(127,29,29,0.6); color: #fecaca;
  border: 1px solid rgba(185,28,28,0.8); }
#hud .btn.danger:hover { background: #7f1d1d; }
#hud .btn:disabled { background: #1e293b; color: #64748b; cursor: not-allowed; }

#hud .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
#hud .grid3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
#hud .tile {
  background: #0f172a; border: 1px solid #1e293b; border-radius: 6px; padding: 8px;
  display: flex; justify-content: space-between; align-items: baseline; font-size: 12px;
}
#hud .tile.center { display: block; text-align: center; }
#hud .tile .k { color: #94a3b8; font-size: 11px; }
#hud .tile .v { font-weight: 700; }
#hud h4 { font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase;
  color: #64748b; margin: 14px 0 7px; }
#hud h4:first-child { margin-top: 0; }

/* paperdoll + bag */
#hud .doll { display: grid; grid-template-columns: repeat(3, 52px); gap: 6px; justify-content: center; }
#hud .cell {
  width: 52px; height: 52px; border-radius: 6px; background: #0f172a;
  border: 1px solid rgba(51,65,85,0.8); position: relative; cursor: pointer;
  display: flex; align-items: center; justify-content: center; font-size: 21px;
}
#hud .cell.empty { color: #475569; font-size: 9px; cursor: default; }
#hud .cell.filled { background: rgba(69,26,3,0.4); border-color: #f59e0b; }
#hud .cell.equipped { background: rgba(6,78,59,0.6); border-color: #10b981; }
#hud .cell .n { position: absolute; right: 3px; bottom: 2px; font-size: 9px;
  color: #fcd34d; font-family: ui-monospace, Menlo, monospace; }
#hud .cell .e { position: absolute; left: 3px; top: 2px; font-size: 8px;
  color: #6ee7b7; font-weight: 700; }
#hud .bag { display: grid; grid-template-columns: repeat(5, 52px); gap: 6px; }

/* list rows: skills, quests, options */
#hud .row-card {
  background: rgba(15,23,42,0.8); border: 1px solid #1e293b; border-radius: 10px;
  padding: 10px; margin-bottom: 8px; display: flex; gap: 10px; align-items: flex-start;
}
#hud .row-card .ic {
  width: 44px; height: 44px; flex: 0 0 44px; border-radius: 8px; background: #020617;
  border: 1px solid #334155; display: flex; align-items: center; justify-content: center;
  font-size: 22px;
}
#hud .row-card .meta { flex: 1; min-width: 0; }
#hud .row-card .meta b { font-size: 13px; }
#hud .row-card .meta p { font-size: 11px; color: #94a3b8; margin: 3px 0; }
#hud .row-card .stats { font-size: 10px; color: #64748b;
  font-family: ui-monospace, Menlo, monospace; display: flex; gap: 10px; flex-wrap: wrap; }
#hud .row-card .stats .mp { color: #7dd3fc; }
#hud .row-card .stats .cd { color: #fcd34d; }

#hud .opt {
  display: block; width: 100%; text-align: left; cursor: pointer;
  background: #0f172a; border: 1px solid #1e293b; border-radius: 10px;
  padding: 11px 12px; margin-bottom: 7px; color: #fcd34d;
  font: inherit; font-size: 12px; font-weight: 600;
}
#hud .opt:hover { background: rgba(69,26,3,0.6); border-color: rgba(245,158,11,0.8); }
#hud .opt:disabled { color: #64748b; cursor: not-allowed; background: #0f172a; }
#hud .greet {
  background: rgba(15,23,42,0.6); border-bottom: 1px solid #1e293b;
  padding: 12px 16px; font-family: ui-serif, Georgia, serif; font-size: 13px; line-height: 1.6;
}
#hud .greet b { color: #fcd34d; display: block; margin-bottom: 5px; }

#hud .class-card {
  background: rgba(15,23,42,0.8); border: 1px solid #1e293b; border-radius: 12px;
  padding: 12px; cursor: pointer; text-align: left; color: inherit; font: inherit;
}
#hud .class-card:hover { border-color: rgba(245,158,11,0.6); }
#hud .class-card.on { background: rgba(69,26,3,0.4); border-color: #f59e0b;
  box-shadow: 0 0 0 2px rgba(251,191,36,0.35); }
#hud .class-card .g { font-size: 22px; }
#hud .class-card b { display: block; margin: 4px 0; }
#hud .class-card p { font-size: 11px; color: #94a3b8; }
#hud .class-card .w { font-size: 10px; color: #fcd34d; margin-top: 6px;
  font-family: ui-monospace, Menlo, monospace; }

#hud .keys { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 12px;
  font-family: ui-monospace, Menlo, monospace; font-size: 11px; }
#hud .keys b { color: #fcd34d; }

#hud .quest-bar { height: 8px; margin: 7px 0 6px; }
#hud .ready { font-size: 10px; color: #6ee7b7; border: 1px solid rgba(16,185,129,0.4);
  background: rgba(6,78,59,0.5); border-radius: 4px; padding: 2px 6px; }

/* ------------------------------------------------------- death veil */
#hud-death {
  position: absolute; inset: 0; pointer-events: auto; display: none;
  align-items: center; justify-content: center; flex-direction: column; gap: 16px;
  background: radial-gradient(circle at 50% 45%, rgba(76,5,25,0.55), rgba(2,6,23,0.9));
}
#hud-death.show { display: flex; }
#hud-death h1 { font-size: 34px; font-weight: 800; letter-spacing: 0.3em;
  color: #fda4af; text-transform: uppercase; }
#hud-death p { color: #cbd5e1; font-size: 13px; }
`;
